import { toNumericRelevance } from '@upright/common/model/company-client-data/types';
import { orderedImpacts, Valence } from '@upright/common/model/types';
import { CompanySBTIDataType } from '../../../impact-lenses/targets/common/types';
import { getShownImpactCategories } from './get-shown-impact-categories';
import { ClientDatum, ClientDatumInfoBoxData, ClientDatumInfoBoxDataWithoutIndex } from './types';

export function getClientDatumInfoBoxes<T extends ClientDatum>(
  data: T[],
  valence: Valence,
  maxBoxes: number,
): Array<ClientDatumInfoBoxData<T>> {
  // filter by valence
  const valenceFiltered = data.filter(d => d.valence === valence);

  // sort
  function sortComparisonValue(d: T) {
    const relevance = 10 - toNumericRelevance(d.relevance);
    const order = d.order ? d.order : 0;
    const impactIndex = orderedImpacts.length - orderedImpacts.indexOf(d.impactCategoryId);
    return relevance * 1000000 + impactIndex * 1000 + order;
  }

  const sorted = valenceFiltered.sort((a, b) => {
    // Sort Science-based target as first:
    if (Object.values(a).includes(CompanySBTIDataType.SBTI_TARGET)) {
      return -1;
    }
    if (Object.values(b).includes(CompanySBTIDataType.SBTI_TARGET)) {
      return 1;
    }
    return sortComparisonValue(a) - sortComparisonValue(b);
  });

  const impacts = getShownImpactCategories(data, valence, maxBoxes);
  const boxes: Array<ClientDatumInfoBoxDataWithoutIndex<T>> = [];

  sorted.forEach(item => {
    if (impacts.indexOf(item.impactCategoryId) !== -1) {
      const wouldBeExtraBox = !!boxes.find(d => d.impact === item.impactCategoryId);
      if (!wouldBeExtraBox) {
        const boxData: T[] = [];
        boxes.push({
          impact: item.impactCategoryId,
          data: boxData,
          likeCount: 0,
          valence,
        });
      }
      const boxInWhichToAdd = [...boxes].reverse().find(d => d.impact === item.impactCategoryId);
      if (!boxInWhichToAdd) {
        throw Error();
      } else {
        boxInWhichToAdd.data.push(item);
      }
    }
  });

  return finalSortBoxes(boxes);
}

function finalSortBoxes<T extends ClientDatum>(data: Array<ClientDatumInfoBoxDataWithoutIndex<T>>) {
  return data
    .sort((a, b) => orderedImpacts.indexOf(a.impact) - orderedImpacts.indexOf(b.impact))
    .map((b, index) => ({
      ...b,
      index,
    }));
}
